import { t } from "@travily/i18n";
import { FullBleed } from "@ui/components/FullBleed";
import { Heading } from "@ui/components/Heading";
import { sectionTab, sectionTabList } from "@ui/components/SectionTabs";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

export type FavoritesTabKey = "marketplace" | "destinations" | "programs";

export interface FavoritesTab {
  key: FavoritesTabKey;
  label: string;
  href: string;
}

export interface FavoritesFrameProps {
  /** Currently-active tab. */
  currentTab: FavoritesTabKey;
  /** Tab definitions; allows the consumer to provide localized labels and hrefs. */
  tabs: FavoritesTab[];
  title: string;
  /** The active tab's content. */
  children: ReactNode;
}

/**
 * Shared frame for the favorites tab pages (`/favorites`, `/favorites/destinations`,
 * `/favorites/programs`). Each tab is its own Inertia route, so tabs render as
 * prefetching links — the URL is the single source of truth for the active tab.
 */
export function FavoritesFrame({ children, currentTab, tabs, title }: FavoritesFrameProps) {
  return (
    <div className="flex flex-col gap-6">
      <Heading.Root color="primary" size="lg">
        <Heading.Title as="h1">{title}</Heading.Title>
      </Heading.Root>
      <FullBleed>
        <nav aria-label={t("components.frames.favorites.nav_label")} className={sectionTabList()}>
          {tabs.map((tab) => {
            const active = tab.key === currentTab;
            return (
              <Link
                aria-current={active ? "page" : undefined}
                className={sectionTab()}
                data-selected={active || undefined}
                href={tab.href}
                key={tab.key}
                prefetch="hover"
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </FullBleed>
      <div>{children}</div>
    </div>
  );
}
